import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PickCard from 'components/PickCard/PickCard';
import Spinner from 'components/Spinner/Spinner';
import Button from 'components/Button/Button';
import LockPick from 'components/LockPick/LockPick';
import { getOurBet } from 'services/picksApi';
import { listGames } from 'services/gamesApi';
import { getOurBetStats } from 'services/picksApi';
import { GROUP_NAMES, ENABLED_SPORTS } from 'utils';
import type { ApiPick, ApiGame, ApiGameListResponse } from 'services/types';
import { t } from '../../helpers/componentHelper';

import './OurBet.scss';
import LockPickTM from 'components/LockPickTM/LockPickTM';

type OurBetComponentProps = {
  userGroups: string[];
};

// Top-level TodaysPick page
function OurBet({ userGroups }: OurBetComponentProps) {
  const navigate = useNavigate();
  const [lockHitRate, setLockHitRate] = useState<number | null>(null);
  const [picks, setPicks] = useState<ApiPick[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One-shot fetch on mount. Any error (auth missing, network) leaves the
  // dial showing a placeholder rather than blocking the page.
  useEffect(() => {
    let runningHits = 0;
    let runningMisses = 0;
    for (const sport of ENABLED_SPORTS) {
      getOurBetStats(sport)
        .then((stats) => {
          runningHits += stats.hits;
          runningMisses += stats.misses;
          setLockHitRate(runningHits / (runningHits + runningMisses));
        })
        .catch(() => {
          // Silent — LockPick renders '—' when successRate is null.
        });
    }
  }, []);

  // only allow access to this page for Elite users
  useEffect(() => {
    if (userGroups.length > 0 && !userGroups.includes(GROUP_NAMES.elite)) {
      navigate('/picks');
    }
  }, [userGroups, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getOurBet()
      .then(async (pickRes) => {
        if (cancelled) return;
        if (pickRes.length === 0) {
          setPicks([]);
          setError(`${t('pages.todaysPick.headerText')}`);
          return;
        }
        // Resolve eventName per unique sport in the response — a single
        // listGames call per sport is cheap and reused across picks.
        const sports = Array.from(new Set(pickRes.map((p) => p.sport)));
        const gameLookup = new Map<string, ApiGame>();
        await Promise.all(
          sports.map(async (sport) => {
            const res: ApiGameListResponse = await listGames({ sport, pageNum: 0 });
            for (const game of res.results) gameLookup.set(game.eventId, game);
          }),
        );
        if (cancelled) return;
        setPicks(
          pickRes.map((p) => {
            const eventName =
              gameLookup.get(p.eventId)?.eventName.replace('Athletics Athletics', 'Athletics') ??
              '';
            return { ...p, eventName };
          }),
        );
        setError(null);
      })
      .catch(() => {
        setError(`${t('pages.ourBet.noResults')}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={'ourBet'}>
      <div className={'ourBet__header'}>
        <div>{`${t('pages.ourBet.headerText')}`}</div>
        <div className={'ourBet__header--subtext'}>{`${t('pages.ourBet.headerSubtext')}`}</div>
      </div>

      <div className={'ourBet__meta'}>
        {loading && picks.length === 0 ? (
          <div className={'ourBet-detail__loading'}>
            <Spinner />
          </div>
        ) : null}
      </div>

      {error && (
        <div className={'ourBet__error'} role={'alert'}>
          {error}
        </div>
      )}

      <div className={'ourBet__list'}>
        {picks.map((pick) => (
          <PickCard key={pick.pickId} pick={pick} />
        ))}

        <div className={'ourBet__button-container'}>
          <Button
            text={`${t('pages.ourBet.buttonText')}`}
            variant="default"
            active={true}
            size="large"
            onClick={() => navigate('/picks')}
          />
        </div>
      </div>

      {lockHitRate !== null && (
        <div className={'ourBet__hitRate'}>
          <div className={'ourBet__hitRate--divider'}></div>
          <div className={'ourBet__hitRate--header'}>{`${t(
            'pages.ourBet.hitRateHeaderText',
          )}`}</div>
          <div className={'ourBet__hitRate--subtext'}>
            <span className={'ourBet__hitRate--subtext italic'}>
              {`${t('pages.ourBet.hitRateHeaderSubText1')}`}
            </span>
            {`${t('pages.ourBet.hitRateHeaderSubText2')}`}
            <LockPickTM />
            {`${t('pages.ourBet.hitRateHeaderSubText3')}`}
          </div>
          <LockPick successRate={lockHitRate} />
        </div>
      )}
    </div>
  );
}

export default OurBet;
